import { EventEmitter, Injectable } from '@angular/core';
import { Subject, firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { EndPoint } from '../config/endpoint';
import { ACCELERATION_EVENT, HOLIDAY_EVENT, PUPIL_EVENT, UNASSIGNED_EVENT } from '../config/events';
import { HttpService } from './http.service';

@Injectable({
  providedIn: 'root',
})
export class PacingPlanService {
  view: string = '';
  currentUrl: string = '';
  grades_id: number = 0;
  subject_id: number = 0;
  isLessonPan = new Subject<boolean>();
  isFirstView = new EventEmitter<boolean>();
  isExpandView = new EventEmitter<boolean>();
  isOpen = new EventEmitter<boolean>();
  isPushed = new EventEmitter<boolean>();
  isStudentLessonPlan = new EventEmitter<object>();
  openCreateAssessments = new EventEmitter<object>();
  changeClassroom = new EventEmitter<object>();
  isOpenExpandView = new EventEmitter<object>();
  currentSubject = new EventEmitter<number>();
  classroomCurrentSubject: any;
  pacingPlanEvents = [];
  showPacingPlanDates = [];
  teacherLessonPlanEvents = [];
  databaseEvents: any;
  testAndReviewDays = [];
  studentCurrentSubject = new EventEmitter<number>();
  studentClassromCurrentSubject: any;

  constructor(private httpService: HttpService) {}

  getCurrentDayPacingPlanForRecommendation() {
    try {
      const filterPacingPlanSectionTitle = this.pacingPlanEvents.filter((x) => {
        if (x.start) {
          const startDate = new Date(x.start); // Ensure x.start is converted to a Date object
          return !isNaN(startDate.getTime()) && startDate.toDateString() === new Date().toDateString();
        }
        return false; // Handle the case where x.start is undefined or null
      });
      return filterPacingPlanSectionTitle;
    } catch (error) {
      throw error;
    }
  }

  async removeExpandObservers() {
    try {
      if (this.isOpenExpandView.observers.length > 1) {
        this.isOpenExpandView.observers.map((ele, index) => {
          if (index < this.isOpenExpandView.observers.length - 1) {
            this.isOpenExpandView.observers[index]['isStopped'] = true;
            this.isOpenExpandView.observers[index]['closed'] = true;
          }
        });

        this.isOpenExpandView.observers.splice(0, this.isOpenExpandView.observers.length - 1);
      }
    } catch (error) {
      throw error;
    }
  }

  async removeObservers(isChangeClassroom = false) {
    try {
      if (isChangeClassroom) {
        if (this.changeClassroom.observers.length > 4) {
          this.changeClassroom.observers.map((ele, index) => {
            if (index < this.changeClassroom.observers.length - 4) {
              this.changeClassroom.observers[index]['isStopped'] = true;
              this.changeClassroom.observers[index]['closed'] = true;
            }
          });

          this.changeClassroom.observers.splice(0, this.changeClassroom.observers.length - 4);
        }
      } else {
        this.isLessonPan.observers.map((ele, index) => {
          if (index !== this.isLessonPan.observers.length - 1) {
            this.isLessonPan.observers[index]['isStopped'] = true;
            this.isLessonPan.observers[index]['closed'] = true;
          }
        });

        this.isLessonPan.observers.splice(0, this.isLessonPan.observers.length - 1);
      }
    } catch (error) {
      throw error;
    }
  }

  async getEasyQuestionsByPacingPlanId(body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.easy_questions_by_id}`, body),
      );
      return this.httpService.decodeToken(response);
    } catch (error) {
      throw error;
    }
  }

  async getDifficultQuestionsByPacingPlanId(body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.difficult_questions_by_id}`, body),
      );
      return this.httpService.decodeToken(response);
    } catch (error) {
      throw error;
    }
  }

  async addDifficultQuestionsToDatabase(body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.difficult_questions}`, body),
      );
      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }

  async addEasyQuestionsToDatabase(body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.easy_questions}`, body),
      );
      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }

  async addPacingPlan(body: object) {
    try {
      return await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.PacingPlan}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async deleteCustomEvent(id: number, status: string) {
    try {
      const url =
        status === 'pacing_plan_event' ? EndPoint.custom_pacing_plans : EndPoint.custom_pacing_plans_test_review_days;
      return await firstValueFrom(this.httpService.delete<any>(`${environment.XANO_SERVER_URL}${url}/${id}`));
    } catch (error) {
      throw error;
    }
  }

  async addMultipleCustomEventsInDatabase(isPacingPlan = false, body) {
    try {
      const url = isPacingPlan ? EndPoint.custom_pacing_plans : EndPoint.custom_pacing_plans_test_review_days;
      return await firstValueFrom(this.httpService.post<any>(`${environment.XANO_SERVER_URL}${url}`, body));
    } catch (error) {
      throw error;
    }
  }

  async addMultipleGlobalPacingPlan(body) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.GlobalPacingPlan}`, body),
      );
      return this.httpService.decodeUserToken(response);
    } catch (error) {
      throw error;
    }
  }

  async getCustomEventsAndPacingPlansBySubjectId(queryParams: any) {
    try {
      return await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.custom_pacing_plans}`, {
          params: queryParams,
        }),
      );
    } catch (error) {
      throw error;
    }
  }

  async getGlobalPacingPlanBySubjectAndGradeId(queryParams: any) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.GlobalPacingPlan}`, {
          params: queryParams,
        }),
      );
      return queryParams && queryParams.page
        ? this.httpService.decodeTokenPagination(response)
        : this.httpService.decodeToken(response);
    } catch (error) {
      throw error;
    }
  }

  async getRecommendation(queryParams: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.recommendation}`, {
          params: queryParams,
        }),
      );
      return this.httpService.decodeToken(response);
    } catch (error) {
      throw error;
    }
  }

  async getRecommendationsForClassroom(queryParams: any) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.get_recommendation_by_grade_id_and_subject_id}`,
          {
            params: queryParams,
          },
        ),
      );
      return this.httpService.decodeTokenPagination(response);
    } catch (error) {
      throw error;
    }
  }

  async addRecommendation(body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.recommendation}`, body),
      );
      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }

  async getGlobalPacingPlansUsingIds(body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.global_pacing_plans_by_ids}`, body),
      );
      return this.httpService.decodeUserToken(response);
    } catch (error) {
      throw error;
    }
  }

  async updatePacingPlan(id: number, body: object) {
    try {
      return await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.PacingPlan}/${id}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async getAllPacingPlan(queryParams: object) {
    try {
      return await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.PacingPlan}`, {
          params: queryParams,
        }),
      );
    } catch (error) {
      throw error;
    }
  }

  // async getPacingPlanByUserId(queryParams: object) {
  //   try {
  //     return await firstValueFrom(
  //       this.httpService.get<any>(
  //         `${environment.XANO_SERVER_URL}${EndPoint.PacingPlanByUserId}`,
  //         {
  //           params: queryParams
  //         }
  //       )
  //     );
  //   } catch (error) {
  //     throw error;
  //   }
  // }

  async getPacingPlanForManualClassroomByUserId(queryParams: object) {
    try {
      return await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.PacingPlanForManualClassroomByUserId}`, {
          params: queryParams,
        }),
      );
    } catch (error) {
      throw error;
    }
  }

  async getPacingGlobalPlanById(id: number) {
    try {
      return await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.GlobalPacingPlan}/${id}`),
      );
    } catch (error) {
      throw error;
    }
  }

  async getPacingPlanById(id: number) {
    try {
      return await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.PacingPlan}/${id}`),
      );
    } catch (error) {
      throw error;
    }
  }

  async deletePacingPlan(id: number) {
    try {
      return await firstValueFrom(
        this.httpService.delete<any>(`${environment.XANO_SERVER_URL}${EndPoint.PacingPlan}/${id}`),
      );
    } catch (error) {
      throw error;
    }
  }

  async deleteMultiplePacingPlans(body) {
    try {
      return await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.deleteMultiplePacingPlan}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async updateMultiplePacingPlan(body) {
    try {
      return await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.update_multiple_pacing_plan}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async updateMultipleTeacherPacingPlan(body) {
    try {
      return await firstValueFrom(
        this.httpService.post<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.update_multiple_teacher_pacing_plans_events}`,
          body,
        ),
      );
    } catch (error) {
      throw error;
    }
  }

  async addTeacherPacingPlan(body) {
    try {
      return await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.teacher_pacing_plan}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async getTeacherPacingPlan(queryParams: object) {
    try {
      const token = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.teacher_pacing_plan}`, {
          params: queryParams,
        }),
      );
      return this.httpService.decodeUserToken(token);
    } catch (error) {
      throw error;
    }
  }

  async deleteAllPacingPlans(body) {
    try {
      return await firstValueFrom(
        this.httpService.post<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.delete_global_pacing_plan_by_subject_grade}`,
          body,
        ),
      );
    } catch (error) {
      throw error;
    }
  }

  async getMultiplePacingPlanById(queryParams) {
    try {
      const token = await firstValueFrom(
        this.httpService.post<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.get_multiple_pacing_plan_by_ids}`,
          queryParams,
        ),
      );
      return token;
      // return this.httpService.decodeUserToken(token);
    } catch (error) {
      throw error;
    }
  }

  async addCustomStandards(body) {
    try {
      return await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.teacher_custom_pacing_plans}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async getCustomAddedStandards(queryParams) {
    try {
      return await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.teacher_custom_pacing_plans}`, {
          params: queryParams,
        }),
      );
    } catch (error) {
      throw error;
    }
  }

  async addCustomGlobalPacingPlan(body) {
    try {
      return await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.custom_global_pacing_plan}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async getDeletedStandardPerClassroomSubject(queryParams: any) {
    try {
      return await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.deleted_teacher_standard_dates}`, {
          params: queryParams,
        }),
      );
    } catch (error) {
      throw error;
    }
  }

  async deleteMultipleDeletedStandards(body: any) {
    try {
      return await firstValueFrom(
        this.httpService.post<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.deleted_teacher_standard_dates_delete_using_ids}`,
          body,
        ),
      );
    } catch (error) {
      throw error;
    }
  }

  async addDeletedStandardWithDate(body: any) {
    try {
      return await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.deleted_teacher_standard_dates}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async getCustomGlobalPacingPlan(queryParams) {
    try {
      return await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.custom_global_pacing_plan}`, {
          params: queryParams,
        }),
      );
    } catch (error) {
      throw error;
    }
  }

  async addMultipleGlobalPacingPlansInDatabase(body) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.add_multiple_custom_global_pacing_plans}`,
          body,
        ),
      );
      return response;
      // return this.httpService.decodeUserToken(response);
    } catch (error) {
      throw error;
    }
  }

  async deleteTeacherPacingPlan(id: number) {
    try {
      return await firstValueFrom(
        this.httpService.delete<any>(`${environment.XANO_SERVER_URL}${EndPoint.teacher_pacing_plan}/${id}`),
      );
    } catch (error) {
      // throw error;
    }
  }

  async deleteTeacherPacingPlanForDays(body) {
    try {
      return await firstValueFrom(
        this.httpService.post<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.delete_multiple_teacher_pacing_plans}`,
          body,
        ),
      );
    } catch (error) {
      throw error;
    }
  }

  async addMultipleTeacherPacingPlans(body) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.add_multiple_teacher_pacing_plans}`, body),
      );
      return response;
      // return this.httpService.decodeUserToken(response);
    } catch (error) {
      throw error;
    }
  }
  async deleteTestReviewDays(body) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.deleted_test_review_days}`, body),
      );
      return response;
      // return this.httpService.decodeUserToken(response);
    } catch (error) {
      throw error;
    }
  }
  async checkedAlreadyDeletedEvents(body) {
    try {
      return await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.deleted_test_review_days}`, {
          params: body,
        }),
      );
    } catch (error) {
      throw error;
    }
  }
  async deleteTestAndReviewDay(id: number) {
    try {
      return await firstValueFrom(
        this.httpService.delete<any>(`${environment.XANO_SERVER_URL}${EndPoint.deleted_test_review_days}/${id}`),
      );
    } catch (error) {
      throw error;
    }
  }

  async updateDeletedTestAndReviewDay(id: number, body: object) {
    try {
      return await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.deleted_test_review_days}/${id}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  // Refactoing code
  setEventsAsCurrentYearEvents(currentCalendar: any[]) {
    const currentYear = new Date().getFullYear();

    currentCalendar[0].holidays.forEach((x) => {
      x.end = new Date(x.end).setFullYear(currentYear);
      x.start = new Date(x.start).setFullYear(currentYear);
    });

    currentCalendar[0].acceleration.forEach((x) => {
      x.end = new Date(x.end).setFullYear(currentYear);
      x.start = new Date(x.start).setFullYear(currentYear);
    });

    currentCalendar[0].pupil_free.forEach((x) => {
      x.end = new Date(x.end).setFullYear(currentYear);
      x.start = new Date(x.start).setFullYear(currentYear);
    });

    currentCalendar[0].bank_days.forEach((x) => {
      x.end = new Date(x.end).setFullYear(currentYear);
      x.start = new Date(x.start).setFullYear(currentYear);
    });

    currentCalendar[0].unassigned.forEach((x) => {
      x.end = new Date(x.end).setFullYear(currentYear);
      x.start = new Date(x.start).setFullYear(currentYear);
    });
    return currentCalendar;
  }

  getLastDayOfMonthOneYearFromNow(currentDate: Date) {
    const nextYear = new Date(currentDate.getFullYear() + 1, currentDate.getMonth(), 1);

    nextYear.setMonth(nextYear.getMonth() + 1);
    nextYear.setDate(0);

    return nextYear;
  }

  async updateEventsData(currentCalendar: any) {
    try {
      const events = [];

      // Helper function to map events
      const mapEvents = (eventArray: any[], cssClass: string) => {
        eventArray.map((event) =>
          events.push({
            start: new Date(event.start),
            end: new Date(event.end),
            title: event.title,
            cssClass: cssClass,
          }),
        );
      };

      // Mapping each event category
      mapEvents(currentCalendar.holidays, HOLIDAY_EVENT[0].cssClass);
      mapEvents(currentCalendar.pupil_free, PUPIL_EVENT[0].cssClass);
      mapEvents(currentCalendar.acceleration, ACCELERATION_EVENT[0].cssClass);
      mapEvents(currentCalendar.unassigned, UNASSIGNED_EVENT[0].cssClass);

      return events;
    } catch (error) {
      console.error(error); // Handle error
      return [];
    }
  }
  async setDateToGlobalPacingPlanEvents(pacingPlanEvents: any[], classroom: any, dates: any[]) {
    try {
      if (classroom.isManual) {
        return pacingPlanEvents.map((x, i) => {
          x.start = new Date(pacingPlanEvents[i].start_date);
          x.end = new Date(pacingPlanEvents[i].end_date);
          return x;
        });
      } else {
        const filteredDates = dates.filter((x) => x.addEvent === true);
        return pacingPlanEvents.map((x, i) => {
          if (filteredDates[i]) {
            x.start = new Date(filteredDates[i].start);
            x.end = new Date(filteredDates[i].end);
            return x;
          }
        });
      }
    } catch (error) {
      console.error(error); // Log the error
      return [];
    }
  }

  async updateGlobalPacingPlan(updateGlobalPacingPlanData: any) {
    try {
      return await firstValueFrom(
        this.httpService.put<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.update_global_pacing_plan_data}`,
          updateGlobalPacingPlanData,
        ),
      );
    } catch (error) {}
  }

  async addMultipleCustomPacingPlansInDatabase(body) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.add_multiple_custom_pacing_plans}`, body),
      );
      return response;
      // return this.httpService.decodeUserToken(response);
    } catch (error) {
      // console.log(error);
    }
  }
  async addMultipleCustomPacingPlansTestAndReviewDaysInDatabase(body) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.add_multiple_custom_pacing_plans_test_and_review_days}`,
          body,
        ),
      );
      return response;
      // return this.httpService.decodeUserToken(response);
    } catch (error) {
      // console.log(error);
    }
  }
  async checkIfLessonPlanExistForDay(queryParams: any) {
    try {
      return await firstValueFrom(
        this.httpService.post<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.teacher_lesson_plan_per_day}`,
          queryParams,
        ),
      );
    } catch (error) {
      throw error;
    }
  }
  async addBulkTestAndReviewDay(body) {
    try {
      const token = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.add_bulk_test_and_review_records}`, body),
      );

      return token;
      // return this.httpService.decodeToken(token);
    } catch (error) {
      throw error;
    }
  }

  async updateMultipleTeacherPacingPlansExtras(body) {
    try {
      const token = await firstValueFrom(
        this.httpService.post<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.update_multiple_teacher_pacing_plans}`,
          body,
        ),
      );

      return token;
      // return this.httpService.decodeToken(token);
    } catch (error) {
      throw error;
    }
  }

  async getAssessmentMasteryCalculation(body) {
    try {
      return await firstValueFrom(
        this.httpService.post<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.get_assessment_mastery_calculation}`,
          body,
        ),
      );
    } catch (error) {
      throw error;
    }
  }

  // async assignDatesToGlobalPacingPlanEvents(dates: any[], pacingPlanEvents: any[]): Promise<any[]> {
  //   const filteredDates = dates.filter((date) => date.addEvent);

  //   return pacingPlanEvents.map((x, i) => {
  //     if (filteredDates[i]) {
  //       if (x.teacher_custom_pacing_plans_id) {
  //         x.start = new Date(x.start);
  //         x.end = new Date(x.end);
  //       } else {
  //         x.start = new Date(filteredDates[i].start);
  //         x.end = new Date(filteredDates[i].end);
  //       }
  //       return x;
  //     }
  //   });
  // }

  async assignDatesToGlobalPacingPlanEvents(dates: any[], pacingPlanEvents: any[]): Promise<any[]> {
    const filteredDates = dates.filter((date) => date.addEvent);

    return pacingPlanEvents.map((x, i) => {
      if (filteredDates[i]) {
        if (x.teacher_custom_pacing_plans_id) {
          x.start = new Date(x.start);
          x.end = new Date(x.end);
        } else if (x.localStorageAutoPacingPlanData) {
          x.start = new Date(x.start);
          x.end = new Date(x.end);
        } else {
          x.start = new Date(filteredDates[i].start);
          x.end = new Date(filteredDates[i].end);
        }
        return x;
      }
    });
  }

  async testAssignDatesToGlobalPacingPlanEvents(dates: any[], pacingPlanEvents: any[]): Promise<any[]> {
    const filteredDates = dates.filter((date) => date.addEvent);
    if (filteredDates.length === 0) return pacingPlanEvents; // Return as-is if no dates are available

    let currentIndex = 0;
    // let lastStartDate: string | null = null;
    const startDateMap = new Map<string, number>(); // Maps start_date to an index in filteredDates

    return pacingPlanEvents.map((event) => {
      if (!startDateMap.has(event.start_date)) {
        startDateMap.set(event.start_date, Math.min(currentIndex, filteredDates.length - 1));
        currentIndex++; // Move to the next available date only when encountering a new start_date
      }

      const assignedIndex = startDateMap.get(event.start_date)!;

      if (filteredDates[assignedIndex]) {
        if (!event.teacher_custom_pacing_plans_id) {
          event.start = new Date(filteredDates[assignedIndex].start);
          event.end = new Date(filteredDates[assignedIndex].end);
        } else {
          event.start = new Date(event.start);
          event.end = new Date(event.end);
        }
      }

      return event;
    });
  }

  async assignDatesToSortedEvents(dates, pacingPlanEvents) {
    const filteredDates = dates.filter((date) => date.addEvent);
    if (filteredDates.length === 0) return pacingPlanEvents; // Return as-is if no dates are available

    let currentIndex = 0;
    const startDateMap = new Map<string, number>(); // Maps start_date to an index in filteredDates

    return pacingPlanEvents.map((event) => {
      // Use start_date for pacing events and start for test-review events
      const key = event.start_date || event.start;

      // Preserve original dates before modifying them
      event.old_start = key ? new Date(key) : null;
      event.old_end = key ? new Date(key) : null;

      if (!startDateMap.has(key)) {
        startDateMap.set(key, Math.min(currentIndex, filteredDates.length - 1));
        currentIndex++; // Move to the next available date only when encountering a new key
      }

      const assignedIndex = startDateMap.get(key)!;

      if (filteredDates[assignedIndex]) {
        if (event.type === 'pacing' && !event.teacher_custom_pacing_plans_id) {
          // Handle pacing events without teacher_custom_pacing_plans_id
          event.start = new Date(filteredDates[assignedIndex].start);
          event.end = new Date(filteredDates[assignedIndex].end);
        } else if (event.type === 'test-review') {
          // Handle test-review events
          event.start = new Date(filteredDates[assignedIndex].start);
          event.end = new Date(filteredDates[assignedIndex].end);
        } else {
          // Handle pacing events with teacher_custom_pacing_plans_id
          event.start = new Date(event.start);
          event.end = new Date(event.end);
        }
      }

      return event;
    });
  }

  /**
   * @description Deletes multiple custom pacing standards.
   * @param body The request body containing the IDs of the pacing standards to delete.
   * @returns A promise that resolves to the response from the server.
   */
  async deleteMultipleCustomPacingStandards(body: any) {
    try {
      return await firstValueFrom(
        this.httpService.post<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.deleted_custom_pacing_plans_using_ids}`,
          body,
        ),
      );
    } catch (error) {
      throw error;
    }
  }
}
